import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../ui/card";
import { Button } from "../../../../ui/button";
import {
  DollarSignIcon,
  EyeIcon,
  Loader2Icon,
  InfoIcon,
} from "lucide-react";
import {
  GenerationConfig,
  AVG_INPUT_TOKENS_PER_EXAMPLE,
  AVG_OUTPUT_TOKENS_NO_REASONING,
  AVG_OUTPUT_TOKENS_WITH_REASONING,
  formatTokenCount,
} from "./types";

interface CostLineItem {
  key: string;
  label: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  usd: number;
  note?: string;
}

interface CostEstimate {
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  isEstimatedPricing: boolean;
  lineItems?: CostLineItem[] | null;
  assumptions?: string[] | null;
}

interface StepPreviewProps {
  config: GenerationConfig;
  costEstimate: CostEstimate | null;
  teacherDisplayName: string;
  canPreview: boolean;
  previewLoading: boolean;
  onGeneratePreview: () => void;
}

const StepPreview: React.FC<StepPreviewProps> = ({
  config,
  costEstimate,
  teacherDisplayName,
  canPreview,
  previewLoading,
  onGeneratePreview,
}) => {
  const useProdSeeds = config.generation_use_prod_seeds;

  const isStructured = config.generation_mode === "structured";
  const previewHint = isStructured
    ? useProdSeeds
      ? "Select a teacher, at least 5 production seeds, and define a user message template."
      : "Select a teacher and define a user message template to enable preview."
    : useProdSeeds
    ? "Select a teacher and at least 5 production seeds to enable preview."
    : "Select a teacher and at least one domain to enable preview.";

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-4 py-3">
        <InfoIcon className="w-4 h-4 text-maitai-green mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground font-gc-reg">
          Last check before naming the dataset: estimated cost (teacher, seed model, supporting workflows) and sample outputs.
        </p>
      </div>

      {costEstimate && config.generation_model && config.generation_size > 0 && (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <DollarSignIcon className="w-5 h-5 text-maitai-green" />
              Estimated cost (full run)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground font-gc-reg">Input tokens (total)</div>
                <div className="text-lg font-gc-med text-foreground">{formatTokenCount(costEstimate.inputTokens)}</div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground font-gc-reg">Output tokens (total)</div>
                <div className="text-lg font-gc-med text-foreground">{formatTokenCount(costEstimate.outputTokens)}</div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground font-gc-reg">Estimated total</div>
                <div className="text-lg font-gc-demi-bold text-maitai-green">~${costEstimate.costUsd.toFixed(2)}</div>
              </div>
            </div>

            {costEstimate.lineItems && costEstimate.lineItems.length > 0 && (
              <div className="rounded-md border border-border overflow-hidden mb-4">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left px-3 py-2 font-gc-med text-muted-foreground">Component</th>
                      <th className="text-right px-3 py-2 font-gc-med text-muted-foreground">Calls</th>
                      <th className="text-right px-3 py-2 font-gc-med text-muted-foreground">~USD</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {costEstimate.lineItems.map((row) => (
                      <tr key={row.key}>
                        <td className="px-3 py-2 text-foreground">
                          <div className="font-gc-med">{row.label}</div>
                          {row.note ? (
                            <div className="text-muted-foreground font-gc-reg mt-0.5">{row.note}</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {row.calls.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-gc-med">${row.usd.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-2">
              <div className="flex items-start gap-2">
                <InfoIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  {costEstimate.lineItems && costEstimate.lineItems.length > 0 ? (
                    <>
                      Heuristic breakdown from generation job structure (chains, seed vs teacher vs
                      auxiliary workflows). Teacher row uses average tokens per completion
                      {teacherDisplayName ? ` (${teacherDisplayName})` : ""}.
                      {costEstimate.isEstimatedPricing && " Some pricing uses defaults."} Actual cost may vary.
                    </>
                  ) : (
                    <>
                      Rough fallback: teacher only, ~{AVG_INPUT_TOKENS_PER_EXAMPLE} input /{" "}
                      {config.generation_include_reasoning ? AVG_OUTPUT_TOKENS_WITH_REASONING : AVG_OUTPUT_TOKENS_NO_REASONING}{" "}
                      output tokens per target example
                      {teacherDisplayName ? ` — ${teacherDisplayName}` : ""}.
                      {costEstimate.isEstimatedPricing && " Pricing approximate."}
                    </>
                  )}
                </p>
              </div>
              {costEstimate.assumptions && costEstimate.assumptions.length > 0 && (
                <ul className="text-[11px] text-blue-800/90 dark:text-blue-200/90 list-disc pl-4 space-y-0.5">
                  {costEstimate.assumptions.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      <Card className="border-border">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="text-sm font-gc-med text-foreground flex items-center gap-2">
                <EyeIcon className="w-4 h-4 text-maitai-green" />
                Preview Generated Examples
              </div>
              <p className="text-xs text-muted-foreground">
                Generate sample examples with the teacher to verify output quality before committing.
              </p>
            </div>
            <Button variant="outline" onClick={onGeneratePreview} disabled={previewLoading || !canPreview} className="gap-2">
              {previewLoading ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <EyeIcon className="w-4 h-4" />}
              {previewLoading ? "Generating..." : "Generate Preview"}
            </Button>
          </div>
          {!canPreview && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-3 flex items-center gap-1.5">
              <InfoIcon className="w-3 h-3 shrink-0" />
              {previewHint}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StepPreview;
